/* FILE: ./frontend/src/features/payments/SubscriptionManager.jsx */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; 

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

const CANADIAN_PROVINCES = [
  { code: 'AB', name: 'Alberta (5% GST)' },
  { code: 'BC', name: 'British Columbia (5% GST)' },
  { code: 'MB', name: 'Manitoba (5% GST)' },
  { code: 'NB', name: 'New Brunswick (15% HST)' },
  { code: 'NL', name: 'Newfoundland & Labrador (15% HST)' },
  { code: 'NT', name: 'Northwest Territories (5% GST)' },
  { code: 'NS', name: 'Nova Scotia (15% HST)' },
  { code: 'NU', name: 'Nunavut (5% GST)' },
  { code: 'ON', name: 'Ontario (13% HST)' },
  { code: 'PE', name: 'Prince Edward Island (15% HST)' },
  { code: 'QC', name: 'Quebec (5% GST)' },
  { code: 'SK', name: 'Saskatchewan (5% GST)' },
  { code: 'YT', name: 'Yukon (5% GST)' }
];

export default function SubscriptionManager() {
  const { t } = useTranslation(); 
  const { user, userProfile, refreshContext } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); 
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedModules, setSelectedModules] = useState([]);
  
  // ⚡ User-defined quantity for $39 RFQ Reply Credits
  const [creditQuantity, setCreditQuantity] = useState(1);

  // ⚡ Place of Supply Billing Province (Pre-filled from profile or /api/geo, editable by user)
  const [billingProvince, setBillingProvince] = useState('ON');

  const pricing = {
    rfq_credit_single: { id: 'rfq_credit_single', label: 'RFQ Reply Credits ($39 / credit)', price: 39, type: 'one_time' },
    analytics: { id: 'analytics', label: 'Module A: Supplier Analytics', price: 99, type: 'recurring' },
    premium_supplier: { id: 'premium_supplier', label: 'Module B: Premium Supplier', price: 199, type: 'recurring' },
    pro_buyer_standard: { id: 'pro_buyer_standard', label: 'Pro Buyer (Standard)', price: 99, type: 'recurring' },
    pro_buyer_volume: { id: 'pro_buyer_volume', label: 'Pro Buyer (High Volume)', price: 199, type: 'recurring' }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const modulesParam = queryParams.get('modules');
    const qtyParam = queryParams.get('qty');
    if (modulesParam) {
      setSelectedModules(modulesParam.split(','));
    }
    if (qtyParam && !isNaN(parseInt(qtyParam, 10))) {
      setCreditQuantity(Math.max(1, parseInt(qtyParam, 10)));
    }
  }, [location.search]);

  // ⚡ Pre-fill Billing Province from userProfile or Vercel /api/geo
  useEffect(() => {
    const validCodes = CANADIAN_PROVINCES.map(p => p.code);
    if (userProfile?.province && validCodes.includes(userProfile.province.toUpperCase())) {
      setBillingProvince(userProfile.province.toUpperCase());
      return;
    }

    const detectGeoProvince = async () => {
      try {
        const res = await fetch('/api/geo');
        if (res.ok) {
          const data = await res.json();
          const detected = (data.province || '').toUpperCase();
          if (validCodes.includes(detected)) {
            setBillingProvince(detected);
          }
        }
      } catch (e) {
        // Fallback remains ON if offline
      }
    };
    detectGeoProvince();
  }, [userProfile]);

  const toggleModule = (modId) => {
    setSelectedModules(prev => {
      let newSelection = [...prev];

      if (newSelection.includes(modId)) {
        newSelection = newSelection.filter(id => id !== modId);
      } else {
        newSelection.push(modId);
      }

      if (modId === 'pro_buyer_standard' && newSelection.includes('pro_buyer_standard')) {
        newSelection = newSelection.filter(id => id !== 'pro_buyer_volume');
      } else if (modId === 'pro_buyer_volume' && newSelection.includes('pro_buyer_volume')) {
        newSelection = newSelection.filter(id => id !== 'pro_buyer_standard');
      }

      // ⚡ FIX: Premium Supplier grants 1 month free Pro Buyer (Std), but DOES NOT lock out Analytics. They are decoupled.
      if (modId === 'premium_supplier') {
        const isTurningOn = !prev.includes('premium_supplier');
        if (isTurningOn) {
          if (!newSelection.includes('pro_buyer_standard') && !newSelection.includes('pro_buyer_volume')) {
            newSelection.push('pro_buyer_standard');
          }
        } else {
          newSelection = newSelection.filter(id => id !== 'pro_buyer_standard');
        }
      }

      return newSelection;
    });
  };

  const calculateTotals = () => {
    let recurring = 0;
    let oneTime = 0;
    const hasPremium = selectedModules.includes('premium_supplier');
    const safeQty = Math.max(1, parseInt(creditQuantity, 10) || 1);

    selectedModules.forEach(id => {
      if (!pricing[id]) return;
      if (id === 'pro_buyer_standard' && hasPremium) {
        // Skip adding to total, the 1-month trial handles this in Helcim
      } else if (id === 'rfq_credit_single') {
        oneTime += pricing[id].price * safeQty;
      } else {
        if (pricing[id].type === 'recurring') {
          recurring += pricing[id].price;
        } else {
          oneTime += pricing[id].price;
        }
      }
    });

    const subtotal = recurring + oneTime;
    const taxRate = ['NS', 'NB', 'NL', 'PE'].includes(billingProvince)
      ? 0.15
      : (billingProvince === 'ON' ? 0.13 : 0.05);
    const taxAmount = parseFloat((subtotal * taxRate).toFixed(2));
    const grandTotal = parseFloat((subtotal + taxAmount).toFixed(2));

    return { recurring, oneTime, total: subtotal, taxRate, taxAmount, grandTotal };
  };

  const { recurring, oneTime, total, taxAmount, grandTotal } = calculateTotals();

  useEffect(() => {
    const scriptId = 'helcim-pay-script';
    if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://secure.helcim.app/helcim-pay/services/start.js';
        script.async = true;
        document.body.appendChild(script);
    }
  }, []);

  // ⚡ Helper: Compute effective expiration date for honest user messaging
  const getEffectiveSupplierExpiry = () => {
    if (userProfile?.supplier_expires_at && new Date(userProfile.supplier_expires_at) > new Date()) {
      return new Date(userProfile.supplier_expires_at);
    }
    return new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  };

  const handleCheckout = async () => {
    if (selectedModules.length === 0) return alert(t('subscription.err_select_tool'));

    if (!user) {
      navigate('/login', { state: { returnTo: location.pathname + location.search } });
      return;
    }

    // ⚡ Honest Expiration Warning if purchasing a-la-carte credits near the end of an active cycle
    if (selectedModules.includes('rfq_credit_single') && !selectedModules.includes('premium_supplier')) {
      const expiryDate = getEffectiveSupplierExpiry();
      const daysLeft = Math.ceil((expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (userProfile?.supplier_expires_at && daysLeft > 0 && daysLeft <= 7) {
        const proceed = window.confirm(
          `⚠️ Please Note: Your current billing cycle ends in ${daysLeft} day(s) on ${expiryDate.toLocaleDateString()}. ` +
          `All a-la-carte RFQ credits expire at the end of the current billing period and do not carry forward to the next month. Do you wish to proceed?`
        );
        if (!proceed) return;
      }
    }

    setLoading(true);
    setError('');

    try {
        const safeQty = Math.max(1, parseInt(creditQuantity, 10) || 1);
        const res = await axios.post(`${API_BASE}/payments/helcim-init`, 
            { selectedModules, totalAmount: total, creditQuantity: safeQty, billingProvince }, 
            { headers: { 'x-user-id': user.id } }
        );

        const { checkoutToken } = res.data;

        if (window.appendHelcimPayIframe) {
            window.appendHelcimPayIframe(checkoutToken);

            const handleHelcimEvent = async (event) => {
                if (event.data?.eventName === `helcim-pay-js-${checkoutToken}`) {
                    if (event.data.eventStatus === 'SUCCESS') {
                        window.removeEventListener('message', handleHelcimEvent);
                        if (window.removeHelcimPayIframe) {
                            try { window.removeHelcimPayIframe(); } catch (e) {}
                        }
                        setSuccessMsg(t('subscription.success_payment'));
                        
                        // ⚡ DUAL-PATH INSTANT CONFIRMATION: Notify backend immediately upon payment success
                        try {
                            await axios.post(`${API_BASE}/payments/helcim-confirm`, 
                                { 
                                    checkoutToken, 
                                    selectedModules, 
                                    creditQuantity: safeQty,
                                    helcimEvent: event.data.eventMessage 
                                }, 
                                { headers: { 'x-user-id': user.id } }
                            );
                        } catch (confirmErr) {
                            console.error("Instant confirmation fallback notice:", confirmErr.message);
                        }

                        await refreshContext(); 
                        setTimeout(() => {
                            setSuccessMsg('');
                            navigate('/account');
                        }, 1500);
                    }
                }
            };
            
            window.addEventListener('message', handleHelcimEvent);
            
        } else {
            setError("The secure payment module is still loading. Please refresh the page and try again.");
        }
    } catch (err) {
        setError(err.response?.data?.error || t('subscription.err_gateway'));
    } finally {
        setLoading(false);
    }
  };

  const hasPremium = selectedModules.includes('premium_supplier');

  // ⚡ Live Current Tier Badges
  const isCurrentlyPremiumSupplier = userProfile?.supplier_tier === 'premium';
  const isCurrentlyAnalytics = Boolean(userProfile?.has_analytics);
  const isCurrentlyProBuyer = userProfile?.buyer_tier === 'pro' || userProfile?.buyer_tier === 'pro_volume';
  const isCurrentlyHighVolBuyer = userProfile?.buyer_volume_tier === 'high_volume' || userProfile?.buyer_tier === 'pro_volume';

  const supplierBadgeText = [
    isCurrentlyPremiumSupplier ? 'PREMIUM' : null,
    isCurrentlyAnalytics ? 'ANALYTICS' : null
  ].filter(Boolean).join(' + ');

  const finalSupplierBadgeText = supplierBadgeText 
    ? `CURRENT: ${supplierBadgeText} (${userProfile?.connection_credits || 0} CREDITS LEFT)` 
    : `CURRENT: FREE (${userProfile?.connection_credits || 0} CREDITS LEFT)`;

  const buyerBadgeText = isCurrentlyProBuyer
    ? (isCurrentlyHighVolBuyer 
        ? `CURRENT: PRO HIGH VOLUME (${userProfile?.ai_audit_credits || 0} AUDITS LEFT)` 
        : `CURRENT: PRO STANDARD (${userProfile?.ai_audit_credits || 0} AUDITS LEFT)`)
    : t('subscription.current_free', 'CURRENT: FREE TIER');

  const effectiveSupplierExpiry = getEffectiveSupplierExpiry();

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 font-sans">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
            <h1 className="text-4xl font-black text-slate-900 tracking-tight">{t('subscription.title')}</h1>
            <p className="text-slate-500 mt-2">{t('subscription.subtitle')}</p>
        </div>

        {/* ⚡ HONEST NON-ROLLOVER & EXPIRATION POLICY BANNER */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 text-xs text-amber-900 leading-relaxed shadow-sm">
          <div className="font-black uppercase tracking-wider text-amber-800 mb-1 flex items-center gap-2">
            <span>📅</span> Monthly Cycle & Non-Rollover Policy
          </div>
          <p>
            All monthly subscription allowances and a-la-carte RFQ reply credits are valid strictly until the end of your current 30-day billing period and <strong>never carry forward to the next month</strong>.
            {userProfile?.supplier_expires_at && new Date(userProfile.supplier_expires_at) > new Date() && (
              <span> Your current Supplier cycle is valid until <strong>{new Date(userProfile.supplier_expires_at).toLocaleDateString()}</strong>.</span>
            )}
            {userProfile?.buyer_expires_at && new Date(userProfile.buyer_expires_at) > new Date() && (
              <span> Your current Buyer cycle is valid until <strong>{new Date(userProfile.buyer_expires_at).toLocaleDateString()}</strong>.</span>
            )}
          </p>
        </div>

        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg font-bold mb-6 border border-red-200 text-center">{error}</div>}
        {successMsg && <div className="bg-green-50 text-green-700 p-4 rounded-lg font-bold mb-6 border border-green-200 text-center">{successMsg}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          
          <div className="space-y-4">
            <div className="flex justify-between items-end mb-4">
               <h2 className="text-xs font-black text-blue-600 uppercase tracking-[0.2em]">{t('subscription.sup_tools')}</h2>
               <span className={`text-[10px] font-bold px-2 py-1 rounded ${isCurrentlyPremiumSupplier || isCurrentlyAnalytics ? 'bg-blue-100 text-blue-800' : 'bg-slate-200 text-slate-600'}`}>
                 {finalSupplierBadgeText}
               </span>
            </div>
            
            <div className="p-4 bg-slate-100 rounded-xl border border-slate-200 text-xs text-slate-600 space-y-1 mb-4">
               <p className="font-bold text-slate-800 mb-1">{t('subscription.incl_free')}</p>
               <p>{t('subscription.free_feat_1')}</p>
               <p>{t('subscription.free_feat_2')}</p>
               <p>{t('subscription.free_feat_3')}</p>
            </div>

            <ModuleCard 
               item={pricing.rfq_credit_single} 
               active={selectedModules.includes('rfq_credit_single')} 
               onToggle={() => toggleModule('rfq_credit_single')}
               desc={`Unlock and respond to active RFQs (covers both chat messages and formal quotes on that RFQ). Valid until ${effectiveSupplierExpiry.toLocaleDateString()} (no rollover).`}
               customQty={creditQuantity}
               onQtyChange={setCreditQuantity}
            />
            <ModuleCard 
               item={pricing.analytics} 
               active={selectedModules.includes('analytics')} 
               onToggle={() => toggleModule('analytics')}
               desc="Unmasks the visitor dashboard. See which buyer companies and provinces are viewing your catalog."
            />
            <ModuleCard 
              item={pricing.premium_supplier} 
              active={selectedModules.includes('premium_supplier')} 
              onToggle={() => toggleModule('premium_supplier')}
              desc="Includes 10 RFQ Reply Credits/mo (no rollover). Unlocks 1,000 SKUs, 2 CAD/BIM models per SKU, and 50MB file uploads."
            />
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-end mb-4">
               <h2 className="text-xs font-black text-emerald-600 uppercase tracking-[0.2em]">{t('subscription.buyer_tools')}</h2>
               <span className={`text-[10px] font-bold px-2 py-1 rounded ${isCurrentlyProBuyer ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                 {buyerBadgeText}
               </span>
            </div>

            <div className="p-4 bg-slate-100 rounded-xl border border-slate-200 text-xs text-slate-600 space-y-1 mb-4">
               <p className="font-bold text-slate-800 mb-1">{t('subscription.incl_free')}</p>
               <p>{t('subscription.buyer_feat_1')}</p>
               <p>{t('subscription.buyer_feat_2')}</p>
            </div>

            <ModuleCard 
              item={pricing.pro_buyer_standard} 
              active={selectedModules.includes('pro_buyer_standard')} 
              onToggle={() => toggleModule('pro_buyer_standard')}
              desc="100 Trade Simulations & Deep AI Compliance Audits/mo (no rollover) + Side-by-Side Supplier Arbitrage Matrix + Exportable PDF Reports."
              isPromoFree={hasPremium} 
            />
            <ModuleCard 
              item={pricing.pro_buyer_volume} 
              active={selectedModules.includes('pro_buyer_volume')} 
              onToggle={() => toggleModule('pro_buyer_volume')}
              desc="300 Trade Simulations & Deep AI Compliance Audits/mo (no rollover) + Side-by-Side Supplier Arbitrage Matrix + Exportable PDF Reports."
            />
          </div>
        </div>

        <div className="sticky bottom-8 bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 flex flex-col md:flex-row justify-between items-center gap-6 z-50">
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">{t('subscription.one_time')}</span>
                <span className="text-xl font-black text-slate-900">${oneTime} <small className="text-xs font-normal">{t('subscription.cad')}</small></span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-blue-500 uppercase block mb-1">{t('subscription.monthly')}</span>
                <span className="text-xl font-black text-blue-600">${recurring} <small className="text-xs font-normal">{t('subscription.cad_mo')}</small></span>
              </div>
              <div className="border-l border-slate-200 pl-6">
                <label htmlFor="billing_province_select" className="text-[10px] font-bold text-slate-500 uppercase block mb-1">
                  Billing Province (Tax)
                </label>
                <select
                  id="billing_province_select"
                  value={billingProvince}
                  onChange={(e) => setBillingProvince(e.target.value)}
                  className="text-xs font-bold text-slate-800 border border-slate-300 rounded-lg px-2.5 py-1.5 bg-slate-50 focus:ring-blue-500 focus:border-blue-500"
                >
                  {CANADIAN_PROVINCES.map(p => (
                    <option key={p.code} value={p.code}>{p.name}</option>
                  ))}
                </select>
                {total > 0 && (
                  <span className="block text-[10px] text-slate-500 mt-1">
                    +${taxAmount.toFixed(2)} Tax = <strong>${grandTotal.toFixed(2)} CAD</strong>
                  </span>
                )}
              </div>
            </div>

            <button 
              onClick={handleCheckout}
              disabled={loading || selectedModules.length === 0}
              className="w-full md:w-auto bg-slate-900 text-white px-10 py-4 rounded-xl font-bold hover:bg-black transition-all shadow-lg disabled:opacity-30"
            >
              {loading ? t('subscription.opening') : t('subscription.subscribe_for', { total: total })}
            </button>
        </div>
      </div>
    </div>
  );
}

function ModuleCard({ item, active, onToggle, desc, isPromoFree, customQty, onQtyChange }) {
  const { t } = useTranslation();
  const isCreditCard = item.id === 'rfq_credit_single';
  const displayPrice = isCreditCard ? item.price * (Math.max(1, parseInt(customQty, 10) || 1)) : item.price;

  return (
    <div 
      onClick={onToggle}
      className={`p-5 rounded-xl border-2 transition-all ${
         active 
            ? 'border-blue-600 bg-blue-50 shadow-md cursor-pointer' 
            : 'border-white bg-white hover:border-slate-200 shadow-sm cursor-pointer'
      }`}
    >
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-bold text-slate-900">{item.label}</h3>
        
        {isPromoFree && active ? (
            <div className="text-right">
                <span className="line-through text-slate-400 text-sm mr-2">${item.price}</span>
                <span className="text-green-600 font-bold">$0 <span className="text-[10px] font-bold uppercase tracking-wider block sm:inline mt-1 sm:mt-0">{t('subscription.first_mo_free')}</span></span>
            </div>
        ) : (
            <span className={`font-black ${active ? 'text-blue-600' : 'text-slate-900'}`}>${displayPrice}</span>
        )}
      </div>
      
      <p className="text-xs text-slate-500 leading-relaxed mb-4">{desc}</p>

      {isCreditCard && (
        <div 
          onClick={(e) => e.stopPropagation()} 
          className="mb-4 p-3 bg-white rounded-lg border border-blue-200 flex items-center justify-between"
        >
          <label htmlFor="credit_qty_input" className="text-xs font-bold text-slate-700 uppercase">
            Number of Credits ($39 each):
          </label>
          <input
            id="credit_qty_input"
            type="number"
            min="1"
            max="500"
            value={customQty}
            onChange={(e) => {
              const val = Math.max(1, parseInt(e.target.value, 10) || 1);
              onQtyChange(val);
              if (!active) onToggle();
            }}
            className="w-20 text-center font-mono font-bold text-sm border border-slate-300 rounded-md p-1.5 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}
      
      <div className="flex items-center gap-2">
        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${active ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300'}`}>
          {active && <span className="text-white text-xs">✓</span>}
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          {active ? t('subscription.selected') : t('subscription.click_add')}
        </span>
      </div>
    </div>
  );
}